import axios from 'axios';
import { PaginatedRequest } from '../types';
import { api } from './api';
import {
  onResponseErrorInterceptor,
  onResponseInterceptor,
} from './interceptors';
import { formatToFormData } from '../utils';
import { DocumentType } from './document';
import { getAccessToken } from '../auth';

export const getFormSetupList = (
  params?: PaginatedRequest,
) =>
  api.request({
    method: 'GET',
    url: '/form_setup/',
    params,
  });

export const getFormSetupFields = (
  params?: PaginatedRequest,
) =>
  api.request({
    method: 'GET',
    url: '/form_setup/get_markup_fields/',
    params,
  });

export const getFilledForm = ({ documentId, formId }) =>
  api.request({
    method: 'GET',
    url: `/project_documents/${documentId}/form/${formId}/`,
  });

export const createFormSetup = data => {
  axios.interceptors.response.use(
    onResponseInterceptor,
    onResponseErrorInterceptor,
  );

  const body = formatToFormData({
    type: data.type,
    description: data.description,
    name: data.name,
    file: data.file,
    isDefault: data.isDefault,
    formMarkup: data.formMarkup,
    fontSize: data.fontSize,
  });

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < data?.jurisdictions?.length; i++) {
    body.append('jurisdictions', data?.jurisdictions[i]);
  }

  if (data?.documents?.length) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < data?.documents?.length; i++) {
      body.append('documents', data?.documents[i]);
    }
  }

  if (data?.reportScope?.length) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < data?.reportScope?.length; i++) {
      body.append('report_scope', data?.reportScope[i]);
    }
  }

  return axios.request<DocumentType>({
    method: 'POST',
    url: '/api/v1/form_setup/',
    data: body,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
};

export const editFormSetup = data => {
  axios.interceptors.response.use(
    onResponseInterceptor,
    onResponseErrorInterceptor,
  );

  const body = formatToFormData({
    type: data.type,
    description: data.description,
    name: data.name,
    file: data.file,
    isDefault: data.isDefault,
    formMarkup: data.formMarkup,
    isDeleted: data.isDeleted,
    fontSize: data.fontSize,
  });

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < data?.jurisdictions?.length; i++) {
    body.append('jurisdictions', data?.jurisdictions[i]);
  }

  if (data?.documents?.length) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < data?.documents?.length; i++) {
      body.append('documents', data?.documents[i]);
    }
  }

  if (data?.reportScope?.length) {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < data?.reportScope?.length; i++) {
      body.append('report_scope', data?.reportScope[i]);
    }
  }

  return axios.request<DocumentType>({
    method: 'PATCH',
    url: `/api/v1/form_setup/${data.id}/`,
    data: body,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
};

/**
 * Render the form through the real server-side PDF pipeline, with every
 * placeholder printing its own field name.
 *
 * `formMarkup` is the editor's CURRENT state, so what comes back reflects
 * un-saved drags — previewing does not commit anything. Omit it to preview
 * what is stored.
 *
 * Multipart + a hand-set Authorization header rather than the shared `api`
 * instance, matching createFormSetup/editFormSetup: that instance's request
 * interceptor rewrites keys and does not leave FormData alone.
 */
export const previewFormSetup = ({ id, formMarkup, fontSize }: PreviewRequest) => {
  axios.interceptors.response.use(
    onResponseInterceptor,
    onResponseErrorInterceptor,
  );

  const body = formatToFormData({ formMarkup, fontSize });

  return axios.request<{ name: string; pdf: string }>({
    method: 'POST',
    url: `/api/v1/form_setup/${id}/preview/`,
    data: body,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
};

interface PreviewRequest {
  id: string | number;
  formMarkup?: string;
  fontSize?: number;
}

const dataUrlToBlob = (dataUrl: string) => {
  const [meta, payload] = dataUrl.split(',');
  const type =
    meta.match(/:(.*?);/)?.[1] || 'application/pdf';
  const binary = atob(payload);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type });
};

/**
 * Same preview, for the Add Form flow where nothing has been saved yet — the
 * uploaded PDF is posted alongside the markup.
 *
 * `dataUrl` is what FileReader handed the form (`getBase64`), so the file does
 * not have to be threaded down through the component tree a second time.
 */
export const previewFormSetupUpload = ({
  dataUrl,
  formMarkup,
  fontSize,
}: PreviewUploadRequest) => {
  axios.interceptors.response.use(
    onResponseInterceptor,
    onResponseErrorInterceptor,
  );

  const body = formatToFormData({ formMarkup, fontSize });
  body.append('file', dataUrlToBlob(dataUrl), 'form.pdf');

  return axios.request<{ name: string; pdf: string }>({
    method: 'POST',
    url: '/api/v1/form_setup/preview_upload/',
    data: body,
    headers: {
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });
};

interface PreviewUploadRequest {
  dataUrl: string;
  formMarkup?: string;
  fontSize?: number;
}

export const deleteFormSetup = (id: number) =>
  api.request({
    method: 'DELETE',
    url: `/form_setup/${id}/`,
  });

  export interface FormSetupType {
    id?: string;
    name: string;
    description: string;
    jurisdictions: Array<number>;
    type: number;
    reportScope?: Array<number>;
    documents?: Array<number>;
    visibleFilename?: string;
    formMarkup: any;
    isDefault: boolean;
    file?: any;
    fontSize?: number;
  }

  export interface FormFillableSetupType {
    id?: string;
    name: string;
    description: string;
    inspectionType: Array<number>;
    jurisdiction: Array<number>;
    type: number;
    reportScope?: Array<number>;
    documents?: Array<number>;
    visibleFilename?: string;
    formMarkup: any;
    isDefault: boolean;
    file?: any;
    fontSize?: number;
  }
